import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { API_CONFIG, API_ENDPOINTS } from '../../../config/api';

interface Accountant {
  Oid: string;
  UserName: string;
  Firstname: string;
  LastName: string;
  Email: string;
  Phone: string;
  IsQualified: boolean;
  IsTaxTrusted: boolean | null;
  Description: string | null;
  BankAccountnum: string | null;
  IsCitizen: boolean | null;
  IsActive: boolean;
  CreatedDate: string;
  CustomerCount?: number;
  PhotoUrl?: string | null;
  Introduction?: boolean | number | string | null;
}

interface AccountantsListProps {
  isOpen: boolean;
  onClose: () => void;
}

const AccountantsList: React.FC<AccountantsListProps> = ({ isOpen, onClose }) => {
  const [accountants, setAccountants] = useState<Accountant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [uploadingPhoto, setUploadingPhoto] = useState<string | null>(null);
  const [photoPreview, setPhotoPreview] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (isOpen) fetchAccountants();
    else {
      setAccountants([]);
      setCurrentIndex(0);
      setError(null);
      setPhotoPreview({});
    }
  }, [isOpen]);

  const fetchAccountants = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.ACCOUNTANTS.BASE}`);
      if (!res.ok) throw new Error('Нягтлан бодогчдын мэдээлэл авахад алдаа гарлаа');
      const data: Accountant[] = await res.json();
      const activeAccountants = data.filter(a => {
        if (!a.IsActive) return false;
        const intro = a.Introduction;
        return intro === true || intro === 1 || intro === '1';
      });
      // Sort by CustomerCount descending (highest first), null/undefined last
      const sorted = activeAccountants.sort((a, b) => {
        const aCount = a.CustomerCount ?? 0;
        const bCount = b.CustomerCount ?? 0;
        // Handle null/undefined - put them at the end
        if (a.CustomerCount === null || a.CustomerCount === undefined) return 1;
        if (b.CustomerCount === null || b.CustomerCount === undefined) return -1;
        return bCount - aCount;
      });
      setAccountants(sorted);
    } catch (e: any) {
      setError(e.message || 'Алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const itemsPerPage = 1;
  const totalPages = Math.ceil(accountants.length / itemsPerPage);
  const currentAccountants = () => {
    const start = currentIndex * itemsPerPage;
    return accountants.slice(start, start + itemsPerPage);
  };

  const handleNext = () => setCurrentIndex(i => (i < totalPages - 1 ? i + 1 : 0));
  const handlePrev = () => setCurrentIndex(i => (i > 0 ? i - 1 : totalPages - 1));

  const handleClose = () => {
    setPhotoPreview({});
    onClose();
  };

  const handlePhotoChange = async (accountantOid: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return setError('Зөвхөн зураг оруулах боломжтой');
    if (file.size > 5 * 1024 * 1024) return setError('Зургийн хэмжээ 5MB-аас их байж болохгүй');

    setUploadingPhoto(accountantOid);
    setError(null);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(prev => ({ ...prev, [accountantOid]: reader.result as string }));
    };
    reader.readAsDataURL(file);

    try {
      const fd = new FormData();
      fd.append('photo', file);
      fd.append('accountantOid', accountantOid);

      const res = await fetch(`${API_CONFIG.BASE_URL}/api/accountant/accountants/upload-photo`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        const er = await res.json().catch(() => ({}));
        throw new Error(er.message || 'Зураг байршуулахад алдаа гарлаа');
      }
      const result = await res.json();
      setAccountants(prev => prev.map(a => (a.Oid === accountantOid ? { ...a, PhotoUrl: result.photoUrl } : a)));
    } catch (e: any) {
      setError(e.message || 'Зураг байршуулахад алдаа гарлаа');
    } finally {
      setUploadingPhoto(null);
    }
  };

  const getAccountantPhoto = (a: Accountant): string | null => {
    if (photoPreview[a.Oid]) return photoPreview[a.Oid];
    if (a.PhotoUrl) return a.PhotoUrl;
    return `/Accountant/${a.UserName}.png`; // public fallback
  };

  const Avatar = ({ a, size = 128 }: { a: Accountant; size?: number }) => {
    const photoUrl = getAccountantPhoto(a);
    const textSize =
      size >= 128 ? 'text-5xl' : size >= 96 ? 'text-3xl' : size >= 64 ? 'text-2xl' : 'text-lg';

    return (
      <div className="relative" style={{ width: size, height: size }}>
        <div
          className={`absolute inset-0 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center font-extrabold text-white ${textSize} shadow-lg`}
          style={{ zIndex: 0 }}
        >
          {a.Firstname?.charAt(0)}
          {a.LastName?.charAt(0)}
        </div>

        {photoUrl && (
          <img
            src={photoUrl}
            alt={`${a.Firstname} ${a.LastName}`}
            className="absolute inset-0 w-full h-full rounded-full object-cover border-2 border-white shadow-lg"
            style={{ zIndex: 1 }}
            onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
          />
        )}
      </div>
    );
  };

  // --- Card variants ---
  const PreviewCard = ({ a }: { a: Accountant }) => (
    <div className="w-28 sm:w-32 opacity-40 hover:opacity-60 cursor-pointer transition-opacity">
      <div className="bg-white/60 backdrop-blur rounded-xl p-3 shadow border border-gray-200">
        <div className="flex flex-col items-center gap-2">
          <Avatar a={a} size={56} />
          <div className="w-full text-center">
            <p className="text-xs font-semibold text-gray-800 truncate">{a.Firstname}</p>
            <p className="text-[11px] text-gray-500 truncate">{a.LastName}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const ActiveCard = ({ a }: { a: Accountant }) => (
    <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl p-6 shadow-xl border border-gray-200 hover:border-blue-300 transition">
      <div className="flex gap-6">
        {/* Left: avatar */}
        <div className="flex-shrink-0">
          <Avatar a={a} size={128} />
        </div>

        {/* Right: info */}
        <div className="flex-1 flex flex-col justify-center gap-3 pl-6">
          <div>
            <h3 className="text-2xl font-extrabold text-gray-800 leading-tight">{a.Firstname}</h3>
            <p className="text-gray-500 -mt-0.5">{a.LastName}</p>
          </div>

          {a.CustomerCount !== undefined && a.CustomerCount !== null && (
            <div className="flex items-center text-purple-600">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
              <span className="text-sm font-medium">{a.CustomerCount} харилцагч</span>
            </div>
          )}

          {a.IsQualified && (
            <div className="flex items-center text-green-600">
              <span className="mr-2">✅</span>
              <span className="text-sm font-medium">Мэргэшсэн нягтлан бодогч</span>
            </div>
          )}

          {a.IsTaxTrusted && (
            <div className="flex items-center text-blue-600">
              <span className="mr-2">⭐</span>
              <span className="text-sm font-medium">Татварын мэргэшсэн нягтлан бодогч</span>
            </div>
          )}

          {a.Email && a.Email !== null && (
            <div className="flex items-center text-gray-700">
              <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              <span className="text-sm font-medium">{a.Email}</span>
            </div>
          )}

          {a.Phone && a.Phone !== null && (
            <div className="flex items-center text-gray-700">
              <svg className="w-4 h-4 mr-2 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
              </svg>
              <span className="text-sm font-medium">{a.Phone}</span>
            </div>
          )}

          {a.Description && a.Description !== null && (
            <div className="pt-2 border-t border-gray-200">
              <p className="text-sm text-gray-600 leading-relaxed">{a.Description}</p>
            </div>
          )}

        

        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  const prevIndex = currentIndex > 0 ? currentIndex - 1 : accountants.length - 1;
  const nextIndex = currentIndex < accountants.length - 1 ? currentIndex + 1 : 0;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold mb-2">🤝 Гэрээт нягтлан бодогч нар</h2>
            <p className="text-blue-100">
              NovaQ-тай хамтран ажилладаг мэргэжлийн нягтлан бодогч нараас санхүү татварын зөвлөгөө авах боломжтой
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:text-gray-200 text-3xl font-bold w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
            aria-label="Хаах"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto py-6">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Ачааллаж байна...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 mx-6">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {!loading && !error && accountants.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">Нягтлан бодогч олдсонгүй</p>
            </div>
          )}

          {!loading && !error && accountants.length > 0 && (
            <div className="space-y-8">
              {/* Carousel row */}
              <div className="relative w-full flex items-center justify-center gap-5">
                {/* Left preview */}
                {accountants.length > 1 && accountants[prevIndex] && (
                  <div onClick={handlePrev} role="button" aria-label="Өмнөх" className="hidden md:block">
                    <PreviewCard a={accountants[prevIndex]} />
                  </div>
                )}

                {/* Active */}
                <div className="max-w-[640px] w-full">
                  {currentAccountants().map((a) => (
                    <ActiveCard key={a.Oid} a={a} />
                  ))}
                </div>

                {/* Right preview */}
                {accountants.length > 1 && accountants[nextIndex] && (
                  <div onClick={handleNext} role="button" aria-label="Дараах" className="hidden md:block">
                    <PreviewCard a={accountants[nextIndex]} />
                  </div>
                )}
              </div>

              {/* Pagination dots */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center pt-4">
                  <div className="flex items-center space-x-2">
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentIndex(i)}
                        aria-label={`Хуудас ${i + 1}`}
                        className={`h-2.5 rounded-full transition-all ${
                          currentIndex === i
                            ? 'bg-blue-600 w-10'
                            : 'bg-gray-300 hover:bg-gray-400 w-2.5'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AccountantsList;
